import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Ruler, Plus, LogOut, Loader2, FolderOpen, Search, X, Brain } from "lucide-react";
import { AiLearningDialog } from "../components/AiLearningDialog";
import { FolderSidebar } from "../components/dashboard/FolderSidebar";
import { ProjectCard } from "../components/dashboard/ProjectCard";
import { TrashView } from "../components/dashboard/TrashView";
import { ProjectInfoDialog } from "../components/ProjectInfoDialog";
import { FILTERS, isDone, searchIndex, matches } from "../lib/projectMeta";

const PAGE = 60;
const FILTER_KEYS = ["all", "active", "done", "materials", "install_wait", "installing", "handed"];

const Stat = ({ id, label, value }) => (
    <span data-testid={`dash-stat-${id}`} className="whitespace-nowrap"><span className="text-slate-500">{label}:</span> <span className="text-slate-100 font-semibold">{value}</span></span>
);

export default function Dashboard() {
    const { user, logout } = useAuth();
    const [learnOpen, setLearnOpen] = useState(false);
    const { t } = useI18n();
    const navigate = useNavigate();
    const [projects, setProjects] = useState(null);
    const [folders, setFolders] = useState([]);
    const [name, setName] = useState("");
    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [view, setView] = useState("all");
    const [filter, setFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [limit, setLimit] = useState(PAGE);
    const [infoFor, setInfoFor] = useState(null);
    const [delFor, setDelFor] = useState(null);
    const [trashCount, setTrashCount] = useState(0);

    const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));
    const load = async () => {
        try {
            const [p, f, tr] = await Promise.all([api.get("/projects"), api.get("/folders"), api.get("/trash")]);
            setProjects(p.data);
            setFolders(f.data);
            setTrashCount(tr.data.length);
        } catch (e) { err(e); setProjects([]); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);
    useEffect(() => { setLimit(PAGE); }, [view, filter, query]);

    const patch = (id, fields) => setProjects((list) => list.map((x) => (x.id === id ? { ...x, ...fields } : x)));
    const savePrefs = async (p, fields) => {
        patch(p.id, fields);
        try { const { data } = await api.put(`/projects/${p.id}/prefs`, fields); patch(p.id, data); } catch (e) { err(e); load(); }
    };
    const move = (p, fid) => {
        const rest = view !== "all" && view !== "favorites" && view !== "archive" ? p.folder_ids.filter((x) => x !== view) : [];
        savePrefs(p, { folder_ids: fid ? [...new Set([...rest, fid])] : rest });
    };
    const copy = async (p, fid) => {
        try { await api.post(`/projects/${p.id}/duplicate`, { folder_id: fid }); toast.success(t("pj_copied")); load(); } catch (e) { err(e); }
    };
    const dropTo = (target, pid) => {
        const p = projects.find((x) => x.id === pid);
        if (!p) return;
        if (target === "archive") savePrefs(p, { archived: true });
        else move(p, target);
    };
    const create = async () => {
        if (!name.trim()) return;
        setCreating(true);
        try {
            const { data } = await api.post("/projects", { name: name.trim() });
            if (folders.some((f) => f.id === view)) await api.put(`/projects/${data.id}/prefs`, { folder_ids: [view] });
            navigate(`/project/${data.id}`);
        } catch (e) { err(e); } finally { setCreating(false); }
    };
    const remove = async (id) => {
        try {
            await api.delete(`/projects/${id}`);
            toast.success(t("trash_moved"), { action: { label: t("trash_open"), onClick: () => setView("trash") } });
            load();
        } catch (e) { err(e); }
    };
    const folderApi = {
        onCreate: async (n) => { try { const { data } = await api.post("/folders", { name: n }); setFolders((f) => [...f, data].sort((a, b) => a.name.localeCompare(b.name))); } catch (e) { err(e); } },
        onRename: async (id, n) => { try { await api.patch(`/folders/${id}`, { name: n }); setFolders((f) => f.map((x) => (x.id === id ? { ...x, name: n } : x))); } catch (e) { err(e); } },
        onDelete: async (id) => {
            try { await api.delete(`/folders/${id}`); setFolders((f) => f.filter((x) => x.id !== id)); if (view === id) setView("all"); load(); } catch (e) { err(e); }
        },
    };

    const indexed = useMemo(() => {
        const fname = Object.fromEntries(folders.map((f) => [f.id, f.name]));
        return (projects || []).map((p) => ({ p, idx: searchIndex(p, p.folder_ids.map((id) => fname[id]).filter(Boolean)) }));
    }, [projects, folders]);
    const live = (projects || []).filter((p) => !p.archived);
    const counts = useMemo(() => {
        const c = { all: live.length, favorites: live.filter((p) => p.favorite).length, archive: (projects || []).length - live.length, trash: trashCount };
        live.forEach((p) => p.folder_ids.forEach((id) => { c[id] = (c[id] || 0) + 1; }));
        return c;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects, trashCount]);
    const results = indexed.filter(({ p, idx }) => {
        if (view === "archive" ? !p.archived : p.archived) return false;
        if (view === "favorites" && !p.favorite) return false;
        if (!["all", "favorites", "archive"].includes(view) && !p.folder_ids.includes(view)) return false;
        return FILTERS[filter](p) && (!query.trim() || matches(idx, query));
    }).map((x) => x.p);

    return (
        <div className="min-h-screen bg-[#0b0f17] text-slate-100">
            <header data-testid="app-header" className="bg-[#0b0f17]/90 backdrop-blur-md border-b border-[#1e293b] sticky top-0 z-50">
                <div className="w-full px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 shrink-0">
                        <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center"><Ruler className="w-5 h-5 text-white" /></div>
                        <div className="font-mono font-bold text-lg">BLIND<span className="text-blue-500">CAD</span></div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <span className="text-sm text-slate-400 hidden sm:block font-mono">{user?.email}</span>
                        <LanguageSelector />
                        <Button data-testid="ai-learning-btn" onClick={() => setLearnOpen(true)} variant="ghost" size="sm" className="text-fuchsia-300 hover:text-fuchsia-100 hover:bg-[#182232]">
                            <Brain className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">{t("al_btn")}</span>
                        </Button>
                        <AiLearningDialog open={learnOpen} onOpenChange={setLearnOpen} />
                        <Button data-testid="logout-button" onClick={logout} variant="ghost" size="sm" className="text-slate-400 hover:text-slate-100 hover:bg-[#182232]">
                            <LogOut className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">{t("nav_logout")}</span>
                        </Button>
                    </div>
                </div>
            </header>

            <main className="w-full px-6 lg:px-10 py-8">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-mono font-extrabold tracking-tight">{t("nav_projects")}</h1>
                        <p className="text-slate-400 mt-1">{t("dash_subtitle")}</p>
                    </div>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-project-button" className="bg-blue-600 hover:bg-blue-500 text-white"><Plus className="w-4 h-4 mr-2" /> {t("dash_new")}</Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#111827] border-[#1e293b] text-slate-100">
                            <DialogHeader><DialogTitle className="font-mono">{t("dash_new")}</DialogTitle></DialogHeader>
                            <div className="py-2">
                                <Input data-testid="new-project-name-input" autoFocus value={name} onChange={(e) => setName(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && create()} placeholder={t("dash_name_ph")} className="bg-[#0b0f17] border-[#1e293b] text-slate-100" />
                            </div>
                            <DialogFooter>
                                <Button data-testid="create-project-confirm" onClick={create} disabled={creating} className="bg-blue-600 hover:bg-blue-500 text-white">
                                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : t("dash_create")}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>

                <div className="flex flex-col lg:flex-row gap-6">
                    <FolderSidebar folders={folders} view={view} setView={setView} counts={counts} onDropTo={dropTo} t={t} {...folderApi} />
                    {view === "trash" ? <TrashView t={t} user={user} onChanged={load} /> : (
                    <section className="flex-1 min-w-0">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                            <Input data-testid="project-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("pj_search_ph")}
                                className="pl-9 pr-9 h-11 bg-[#111827] border-[#1e293b] text-slate-100" />
                            {query && <button data-testid="project-search-clear" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>}
                        </div>
                        <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
                            <div className="flex gap-1.5 flex-wrap">
                                {FILTER_KEYS.map((k) => (
                                    <button key={k} data-testid={`status-filter-${k}`} onClick={() => setFilter(k)}
                                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filter === k ? "bg-blue-600 border-blue-500 text-white" : "border-[#1e293b] text-slate-300 hover:bg-[#182232]"}`}>{t(`pj_flt_${k}`)}</button>
                                ))}
                            </div>
                            <div data-testid="dash-stats" className="flex gap-4 text-xs font-mono flex-wrap">
                                <Stat id="total" label={t("pj_total")} value={live.length} />
                                <Stat id="found" label={t("pj_found")} value={results.length} />
                                <Stat id="active" label={t("pj_active")} value={live.filter((p) => !isDone(p)).length} />
                                <Stat id="done" label={t("pj_done")} value={live.filter(isDone).length} />
                            </div>
                        </div>

                        <div className="mt-5">
                            {projects === null ? (
                                <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>
                            ) : results.length === 0 ? (
                                <div data-testid="no-results" className="border border-dashed border-[#1e293b] rounded-2xl py-16 flex flex-col items-center text-center">
                                    <div className="w-14 h-14 rounded-2xl bg-[#182232] flex items-center justify-center mb-4"><FolderOpen className="w-7 h-7 text-slate-500" /></div>
                                    <h3 className="font-mono font-semibold text-lg">{projects.length ? t("pj_no_results") : t("dash_empty_title")}</h3>
                                    {!projects.length && <p className="text-slate-400 mt-1 max-w-sm">{t("dash_empty_sub")}</p>}
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                                        {results.slice(0, limit).map((p) => (
                                            <ProjectCard key={p.id} p={p} folders={folders} t={t} onOpen={() => navigate(`/project/${p.id}`)}
                                                onPrefs={savePrefs} onMove={move} onCopy={copy} onInfo={() => setInfoFor(p)} onDelete={() => setDelFor(p)}
                                                onRenamed={(n) => patch(p.id, { name: n })} />
                                        ))}
                                    </div>
                                    {results.length > limit && (
                                        <div className="flex justify-center mt-6">
                                            <Button data-testid="show-more" variant="outline" onClick={() => setLimit((l) => l + PAGE)} className="border-[#1e293b] bg-transparent text-slate-200 hover:bg-[#182232]">
                                                {t("pj_show_more", { n: results.length - limit })}
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </section>
                    )}
                </div>
            </main>

            <ProjectInfoDialog open={!!infoFor} onOpenChange={(o) => { if (!o) setInfoFor(null); }} projectId={infoFor?.id} info={infoFor?.info}
                editable={infoFor?.my_role !== "commenter"} onSaved={(info) => patch(infoFor.id, { info })} />
            <AlertDialog open={!!delFor} onOpenChange={(o) => { if (!o) setDelFor(null); }}>
                <AlertDialogContent className="bg-[#111827] border-[#1e293b] text-slate-100">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("dash_del_title")}</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">{t("trash_del_desc", { name: delFor?.name })}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-[#182232] border-[#1e293b] text-slate-200 hover:bg-[#1e293b]">{t("dash_cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid="confirm-delete-project" onClick={() => remove(delFor.id)} className="bg-rose-600 hover:bg-rose-500 text-white">{t("dash_delete")}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
